import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { calculateConversionRate } from '../utils/financialCalculations'

const CustomerAcquisitionModal = ({ isOpen, monthLabel, onClose, onSave, saving, saveError }) => {
  const { t, i18n } = useTranslation('dashboard')
  const [leads, setLeads] = useState('')
  const [customers, setCustomers] = useState('')
  const [validationError, setValidationError] = useState('')
  const firstInputRef = useRef(null)
  const dialogRef = useRef(null)
  const previousFocusRef = useRef(null)

  const leadCount = leads === '' ? 0 : Number(leads)
  const customerCount = customers === '' ? 0 : Number(customers)
  const customersExceedLeads = customers !== '' && customerCount > leadCount
  const conversionRate = calculateConversionRate(leadCount, customerCount)
  const formattedRate = useMemo(
    () => `${new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(conversionRate)}%`,
    [conversionRate, i18n.language]
  )

  useEffect(() => {
    if (!isOpen) return undefined

    previousFocusRef.current = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusTimer = window.setTimeout(() => firstInputRef.current?.focus(), 0)
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) {
        onClose()
        return
      }

      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled)')
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      previousFocusRef.current?.focus?.()
    }
  }, [isOpen, onClose, saving])

  useEffect(() => {
    if (!isOpen) return
    setLeads('')
    setCustomers('')
    setValidationError('')
  }, [isOpen])

  if (!isOpen) return null

  const handleSubmit = (event) => {
    event.preventDefault()
    const parsedLeads = Number(leads)
    const parsedCustomers = Number(customers)

    if (!Number.isSafeInteger(parsedLeads) || parsedLeads < 0 || !Number.isSafeInteger(parsedCustomers) || parsedCustomers < 0) {
      setValidationError(t('customerAcquisitionModal.wholeNumberError'))
      return
    }
    if (parsedCustomers > parsedLeads) {
      setValidationError(t('dataEntry.validation.customersExceedLeads'))
      return
    }
    if (parsedLeads === 0 && parsedCustomers === 0) {
      setValidationError(t('customerAcquisitionModal.enterCountError'))
      return
    }

    setValidationError('')
    onSave({ leads: parsedLeads, customers: parsedCustomers })
  }

  const close = () => {
    if (!saving) onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-[#101828]/55 p-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close()
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-acquisition-dialog-title"
        aria-describedby="customer-acquisition-dialog-description"
        className="my-auto w-full max-w-[760px] rounded-[20px] border border-[#E4E7EC] bg-white p-6 shadow-[0_24px_72px_rgba(16,24,40,0.22)] sm:p-8"
      >
        <form onSubmit={handleSubmit}>
          <div className="flex items-start justify-between gap-5">
            <div>
              <h2 id="customer-acquisition-dialog-title" className="text-xl font-bold tracking-tight text-[#101828] sm:text-2xl">
                {t('customerAcquisitionModal.title')}
              </h2>
              <p id="customer-acquisition-dialog-description" className="mt-2 max-w-[590px] text-sm leading-6 text-[#667085]">
                {t('customerAcquisitionModal.subtitle')}
              </p>
              <p className="mt-1.5 text-xs font-medium text-[#667085]">
                {t('customerAcquisitionModal.addingToMonth', { month: monthLabel })}
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              disabled={saving}
              aria-label={t('customerAcquisitionModal.close')}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#EAECF0] bg-[#F9FAFB] text-[#667085] transition-colors hover:bg-[#F2F4F7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54]/30 disabled:opacity-50"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-7 grid gap-5 sm:grid-cols-2">
            <label className="block text-sm font-medium text-[#344054]">
              {t('dataEntry.fields.leads')}
              <input
                ref={firstInputRef}
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={leads}
                onChange={(event) => { setLeads(event.target.value); setValidationError('') }}
                placeholder="0"
                aria-label={t('dataEntry.fields.leads')}
                aria-invalid={Boolean(validationError) || customersExceedLeads}
                className="mt-2 h-[54px] w-full rounded-xl border border-[#D0D5DD] bg-white px-4 text-base font-normal text-[#101828] outline-none transition-shadow placeholder:text-[#98A2B3] focus:border-[#FF1B54] focus:ring-4 focus:ring-[#FF1B54]/10"
              />
            </label>
            <label className="block text-sm font-medium text-[#344054]">
              {t('dataEntry.fields.customersAcquired')}
              <input
                type="number"
                min="0"
                step="1"
                max={leadCount}
                inputMode="numeric"
                value={customers}
                onChange={(event) => { setCustomers(event.target.value); setValidationError('') }}
                placeholder="0"
                aria-label={t('dataEntry.fields.customersAcquired')}
                aria-invalid={Boolean(validationError) || customersExceedLeads}
                className="mt-2 h-[54px] w-full rounded-xl border border-[#D0D5DD] bg-white px-4 text-base font-normal text-[#101828] outline-none transition-shadow placeholder:text-[#98A2B3] focus:border-[#FF1B54] focus:ring-4 focus:ring-[#FF1B54]/10"
              />
            </label>
          </div>

          <div className="mt-5 rounded-xl border border-[#A6F4C5] bg-white px-5 py-4" aria-live="polite">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-medium text-[#475467]">{t('customerAcquisitionModal.rateLabel')}</span>
              <span className="text-2xl font-bold tracking-tight text-[#078343]">{customersExceedLeads ? '—' : formattedRate}</span>
            </div>
          </div>
          {leadCount > 0 ? (
            <p className="mt-2.5 text-sm leading-5 text-[#667085]">
              {t('customerAcquisitionModal.perHundred', { customers: customerCount, leads: leadCount })}
            </p>
          ) : (
            <p className="mt-2.5 text-sm leading-5 text-[#667085]">{t('customerAcquisitionModal.zeroLeadsHint')}</p>
          )}

          {(validationError || customersExceedLeads || saveError) && (
            <p className="mt-3 text-sm font-medium text-[#B42318]" role="alert">
              {validationError || (customersExceedLeads ? t('dataEntry.validation.customersExceedLeads') : saveError)}
            </p>
          )}

          <div className="mt-7 flex flex-col-reverse gap-3 border-t border-[#EAECF0] pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={close}
              disabled={saving}
              className="inline-flex h-12 items-center justify-center rounded-xl border border-[#D0D5DD] bg-white px-5 text-sm font-semibold text-[#344054] transition-colors hover:bg-[#F9FAFB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#98A2B3]/40 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              disabled={saving || customersExceedLeads || (leadCount === 0 && customerCount === 0)}
              className="inline-flex h-12 items-center justify-center rounded-xl bg-[#FF1B54] px-6 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#E1174C] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FF1B54]/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? t('dataEntry.saving') : t('dataEntry.saveMetrics')}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

export default CustomerAcquisitionModal
